import { useTranslation } from 'react-i18next';
import {
  SourceVariableType,
  VariableValidation,
  WebhookVariableType,
} from '../../types';
import { AUTOMATION_VARIABLE_DATA_TYPES } from 'pages/AutomationEngine/constants';
import {
  useVariableDescriptionContext,
  type VariableErrorType,
} from '../../VariableWizardContext';
import { useCallback, useMemo } from 'react';
import { useInputErrors } from '../useInputErrors';
import { ValidationKeys } from '../ValidationKeys';
import { StackableCols } from '../../styles';
import { SelectNoScrollCapture as Select } from '__components/Inputs/Select';
import {
  AutomationVariableDataType,
  GenericOption,
} from 'pages/AutomationEngine/types';
import { fieldValidate } from '../utilties';
import { getWebhookValueLabel } from '../format-meta-data';

const getDefaultState = (): WebhookVariableType => ({
  value: undefined,
  valueCategory: undefined,
  variableType: AUTOMATION_VARIABLE_DATA_TYPES.string,
  id: Date.now().toString(),
});

export const Component = (
  values: SourceVariableType & {
    sourceErrors: VariableErrorType;
    dataType: AutomationVariableDataType;
    dataSubtype: any;
  }
) => {
  const { t } = useTranslation();

  const { webhookVariable = getDefaultState(), id, sourceErrors } = values;

  const { valueCategory, value } = webhookVariable || {};

  const { updateVariableSource, webhookTriggerContext, errors, setErrors } =
    useVariableDescriptionContext();

  const {
    validate: validateValueCategory,
    hasError: hasErrorValueCategory,
    clearError: clearValueCategoryError,
  } = useInputErrors<ValidationKeys>(
    id,
    'valueCategory',
    sourceErrors,
    errors,
    setErrors
  );

  const {
    validate: validateValue,
    hasError: hasErrorValue,
    clearError: clearValueError,
  } = useInputErrors<ValidationKeys>(
    id,
    'value',
    sourceErrors,
    errors,
    setErrors
  );

  const valueCategoryOptions = useMemo(() => {
    return webhookTriggerContext.webhooks.map(({ name }) => ({
      label: name,
      value: name,
    }));
  }, [webhookTriggerContext]);

  const valueOptions = useMemo(() => {
    const selectedTrigger = webhookTriggerContext.webhooks.find(
      (wh) => wh.name === valueCategory?.value
    );
    if (!selectedTrigger) {
      return [];
    }
    const categories = [];
    const extractorOptions = (selectedTrigger.extractors || []).map((ex) => ({
      value: ex.valueName,
      label: ex.valueName,
    }));

    if (extractorOptions.length > 0) {
      categories.push({
        label: t('Extracted Values'),
        options: extractorOptions,
      });
    }

    if (
      selectedTrigger.extractRawBodyContent ||
      selectedTrigger.extractUrlQueryString
    ) {
      categories.push({
        label: t('Webhook Metadata'),
        options: [
          selectedTrigger.extractUrlQueryString
            ? {
                value: 'meta_query_string',
                label: getWebhookValueLabel('meta_query_string', t),
              }
            : null,
          selectedTrigger.extractRawBodyContent
            ? {
                value: 'meta_body_content',
                label: getWebhookValueLabel('meta_body_content', t),
              }
            : null,
        ].filter(Boolean) as GenericOption[],
      });
    }

    return categories;
  }, [webhookTriggerContext, valueCategory, t]);

  const updateWebhookVariable = useCallback(
    (webhookVariable: WebhookVariableType) => {
      updateVariableSource({
        ...values,
        webhookVariable,
      });
    },
    [updateVariableSource, values]
  );

  const handleValueCategoryChange = useCallback(
    (valueCategory: GenericOption) => {
      updateWebhookVariable({
        ...webhookVariable,
        valueCategory: valueCategory,
        value: undefined,
      });
      clearValueCategoryError('valueCategory');
      clearValueError('value');
    },
    [
      clearValueCategoryError,
      clearValueError,
      webhookVariable,
      updateWebhookVariable,
    ]
  );

  const handleValueChange = useCallback(
    (value: GenericOption) => {
      updateWebhookVariable({
        ...webhookVariable,
        value,
      });
      clearValueError('value');
    },
    [clearValueError, webhookVariable, updateWebhookVariable]
  );

  return (
    <StackableCols columns={3} gutter="20px">
      <Select
        label={t('Trigger')}
        placeholder={t('Choose Trigger')}
        value={valueCategory}
        options={valueCategoryOptions}
        onChange={handleValueCategoryChange}
        validate={validateValueCategory}
        error={hasErrorValueCategory}
        menuInline
        data-qa="webhook-trigger-value-category"
      />
      <Select
        label={t('Field')}
        placeholder={t('Choose Field')}
        value={value}
        options={valueOptions}
        onChange={handleValueChange}
        validate={validateValue}
        error={hasErrorValue}
        menuInline
        data-qa="webhook-trigger-value"
      />
    </StackableCols>
  );
};

export const validate = ({ values, checkRequired, t }: VariableValidation) => {
  const { webhookVariable } = values;
  const res = fieldValidate(
    webhookVariable,
    {
      valueCategory: [
        { type: 'required', fieldType: undefined, isSelect: true },
        {
          type: 'deleted-field',
          fieldType: undefined,
          deletedNameLookup: { fallback: t('Trigger') },
        },
      ],
      value: [
        {
          type: 'required',
          fieldType: undefined,
          isSelect: true,
        },
        {
          type: 'deleted-field',
          fieldType: undefined,
          deletedNameLookup: {
            fallback: t('Extracted Value'),
          },
        },
      ],
    },
    undefined,
    checkRequired,
    t
  );

  return res;
};
